/**
 * Post-paint FIFO queue for the app's analytics work (INP).
 *
 * Analytics (dataLayer.push / DataMappingInterface.init) run synchronously
 * inside tap handlers today, so GTM tag processing lands inside the interaction
 * frame and inflates INP. Routing every event through this single queue moves
 * that work to just after the next paint while preserving relative event order
 * (one queue — deferring only a subset would reorder events). One documented
 * exception: handleLinkClick passes through the wrapper un-queued (see
 * dds-analytics-wrapper.ts) — its internal setTimeout(0) fires before the
 * rAF-gated flush, so a handleLinkClick event can land ahead of app events
 * queued earlier in the same tap. Accepted: queueing it would lose the event
 * on unload, which is worse than a one-frame ordering skew.
 *
 * Scope: this covers events fired from app code AND from @directv-digital
 * components — the webpack alias in next.config.js routes the whole
 * dds.services.analytics-service surface (dtv-button CTA clicks, error-display
 * events, impressions, …) through the wrapper in this folder. The only push
 * that cannot ride the queue is the document-level z1 inline script
 * (lib/sessionai/z1-capture.inline.ts) — it is serialized into HTML and runs
 * before any bundle, so it stays synchronous by construction.
 *
 * Scheduling: requestAnimationFrame + setTimeout(0) = "next paint has
 * committed". Tasks arriving while the document is hidden run inline instead
 * (no paint/INP to protect there, and background timers are throttled or the
 * tab discarded). Two loss-proofing listeners (bound on first enqueue):
 * - capture-phase pagehide → synchronous flush (external navigation)
 * - visibilitychange → hidden → synchronous flush (tab switch / tel: link /
 *   app switch — rAF freezes there and background tabs can be discarded
 *   without pagehide, so waiting for the next frame would strand the queue)
 *
 * Known trade-off (measured-rollout item): a handler that pushes and then
 * navigates synchronously in the same task (e.g. dtv-button's
 * handleClick → window.location.href) now pushes during the pagehide flush
 * when the new document arrives before the next frame. The event still
 * reaches the dataLayer; whether the resulting vendor beacons survive unload
 * depends on their transport (sendBeacon/keepalive do, image pixels may not).
 * The rollout gate for this is the lower-env GA4/Tealium volume-parity day
 * check before any prod flip — see HANDOFF.md.
 *
 * Kill switch: defer is OFF until setAnalyticsPushDeferEnabled(true) is called.
 * The production callers are pages/_app.tsx (pageProps.analyticsDeferMode) and
 * the v3 bootstrap (WAT-12663), both tracing back to the remote per-env toggle in
 * lib/cache/in-memory-cache/analytics-flags.ts. App Router v2 documents never
 * call it, so they keep this module's default of off (see app/layout.tsx).
 * Off = tasks run synchronously, i.e. the exact pre-existing TIMING behavior.
 * Note the switch gates timing only — module resolution still goes through the
 * wrapper either way (the next.config.js alias is not flag-gated).
 *
 * Flipping a tier is an edit to the remote JSON asset — no redeploy, which is
 * the fast revert path WAT-11057 asked for. Two caveats on how fast "fast" is:
 * - Each pod picks the change up on its own refresh cycle, so pods disagree for
 *   up to UF_CACHE_INT minutes (default 5).
 * - The mode is baked into a document at render time, so a flip only affects
 *   NEW document loads. An already-open tab keeps its mode until reload. (No
 *   shared/edge HTML cache extends this: measured 2026-08-06, home and off-home
 *   both serve `cache-control: private, must-revalidate, max-age=5`.)
 */

type AnalyticsTask = () => void;

// Bound at module load, i.e. before utils/suppressConsoleMethods.ts no-ops
// console.error in prod (see WAT-10904) — a queue failure that only logged
// through the suppressed global would be invisible exactly where the flag
// gets flipped on. Static module evaluation always precedes the _app-body /
// ConsoleSuppressor-effect suppression calls, so this captures the real one.
const reportQueueFailure =
  typeof console !== "undefined" ? console.error.bind(console) : undefined;

let deferEnabled = false;
const queue: AnalyticsTask[] = [];
let flushScheduled = false;
let lossGuardsBound = false;

export function setAnalyticsPushDeferEnabled(enabled: boolean): void {
  deferEnabled = enabled;
  if (!enabled) {
    // Never strand queued events when defer turns off. This makes the setter the
    // ONLY public drain, so the contract is on WHERE it may be called from:
    // never inside a render pass and never inside an interaction handler, both
    // of which would flush queued work back into the frame this module exists to
    // protect. Both callers honor that: useAnalyticsDeferArm arms in render —
    // which cannot reach this branch — and the v3 bootstrap sets it in an
    // effect; both disarm only post-commit.
    flushAnalyticsQueue();
  }
}

/**
 * Read-only view of the current defer state.
 *
 * NOT dead code, despite having no production caller. Specs that assert
 * synchronous push ordering use it to PIN their own precondition, so a future
 * change that arms the queue globally fails with a clear cause instead of
 * breaking unrelated payload assertions (see
 * src/utils/cart-summary/fire-cart-summary.integration.spec.ts). It was deleted
 * once as unused and had to be restored when a spec landed on a parallel branch;
 * grep the whole repo, specs included, before removing it again.
 */
export function isAnalyticsPushDeferEnabled(): boolean {
  return deferEnabled;
}

/**
 * Run an analytics task through the shared queue. With defer disabled (or on
 * the server) the task runs synchronously — identical to the legacy path.
 *
 * The `typeof window` guard is what makes the module-mutating caller (_app's
 * render-phase arm, which also runs during SSR) safe on the server: even with
 * the flag set on a shared Node process, server-side tasks always run inline.
 * Do not remove it.
 */
export function runAnalyticsTask(task: AnalyticsTask): void {
  if (typeof window === "undefined" || !deferEnabled) {
    task();
    return;
  }
  if (typeof document === "undefined" || document.hidden) {
    // Hidden documents get no rAF and throttled timers, and the tab can be
    // discarded before either fires — and there is no paint/INP to protect.
    // Drain anything already queued first so FIFO order holds, then run inline.
    // (The document guard also covers exotic window-without-document globals.)
    flushAnalyticsQueue();
    task();
    return;
  }
  queue.push(task);
  bindLossGuards();
  scheduleFlush();
}

/**
 * Drain the queue in FIFO order. One failing task must not drop the rest.
 * Deliberately NOT exported: a public drain would invite callers to flush
 * mid-interaction, reintroducing the tap-frame cost this module removes.
 * (Nested runAnalyticsTask calls made from inside a flush are drained by the
 * same while loop rather than waiting an extra frame.)
 */
function flushAnalyticsQueue(): void {
  flushScheduled = false;
  while (queue.length > 0) {
    const task = queue.shift();
    try {
      task?.();
    } catch (error) {
      reportQueueFailure?.("[AnalyticsDefer] queued analytics task failed", error);
    }
  }
}

function scheduleFlush(): void {
  if (flushScheduled) {
    return;
  }
  flushScheduled = true;
  // Hidden-document tasks never reach here (runAnalyticsTask runs them
  // inline), so this fallback only covers environments without rAF.
  if (typeof requestAnimationFrame !== "function") {
    setTimeout(flushAnalyticsQueue, 0);
    return;
  }
  requestAnimationFrame(() => {
    setTimeout(flushAnalyticsQueue, 0);
  });
}

function bindLossGuards(): void {
  if (lossGuardsBound) {
    return;
  }
  lossGuardsBound = true;
  window.addEventListener("pagehide", () => flushAnalyticsQueue(), true);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      flushAnalyticsQueue();
    }
  });
}
